import React from "react";
import {
  DefaultOpenGraphHeadQuery,
  Maybe,
  OpenGraphMetaTagsFragment,
  SanityOpenGraph,
  SitePageContextAlternateLinks
} from "types/graphql-types";
import { buildSEOImageUrl } from "./utils";

import { jsonLdScriptProps } from "react-schemaorg";
import type { WebSite } from "schema-dts";

import { graphql, useStaticQuery } from "gatsby";
import {
  languageFromCode,
  pickTranslation
} from "src/utils/localization/languages/languages";
import { LanguageCode } from "src/utils/localization/languages/types";
import { isPresent } from "src/utils/checks";

interface GatsbyHeadOpenGraphProps {
  path: string;
  languageCode: LanguageCode;
  alternateLinks: (SitePageContextAlternateLinks | undefined)[] | undefined;
  openGraph?: Maybe<SanityOpenGraph> | Maybe<OpenGraphMetaTagsFragment>;
  altTitle?: Maybe<string>;
  altDescription?: Maybe<string>;
  altImage?: Maybe<string>;
  excludeFromSitemap?: Maybe<boolean>;
  jsonLd?: ReturnType<typeof jsonLdScriptProps>[];
}

const useDefaultOpenGraph = (
  languageCode: LanguageCode
): OpenGraphMetaTagsFragment | undefined => {
  const language = languageFromCode(languageCode);
  const data = useStaticQuery<DefaultOpenGraphHeadQuery>(query);
  let localizedOpenGraph: OpenGraphMetaTagsFragment | undefined = undefined;
  if (data) {
    try {
      const openGraph = data.site?.openGraph;
      const title = pickTranslation<Maybe<string>>(openGraph?.title, language);
      const metaDescription = pickTranslation<Maybe<string>>(
        openGraph?.description,
        language
      );

      localizedOpenGraph = {
        title: { localized: title },
        description: { localized: metaDescription },
        image: openGraph?.image
      };
    } catch {
      localizedOpenGraph = undefined;
    }
  }

  return localizedOpenGraph;
};

const GatsbyHeadOpenGraph: React.FC<
  React.PropsWithChildren<GatsbyHeadOpenGraphProps>
> = ({
  path,
  languageCode,
  alternateLinks = [],
  children,
  openGraph,
  altTitle,
  altDescription,
  altImage,
  jsonLd,
  excludeFromSitemap = false
}) => {
  const defaultOpenGraph = useDefaultOpenGraph(languageCode);
  const image =
    altImage ||
    buildSEOImageUrl(openGraph?.image) ||
    buildSEOImageUrl(defaultOpenGraph?.image);
  const title =
    openGraph?.title?.localized ||
    altTitle ||
    defaultOpenGraph?.title?.localized;
  const metaDescription =
    openGraph?.description?.localized ||
    altDescription ||
    defaultOpenGraph?.description?.localized;

  const pageTitle = `${title} | Silversea`;

  const metas = [
    {
      key: "website",
      property: "og:type",
      content: "website"
    },
    {
      key: "twitter-summary",
      name: "twitter:card",
      content: "summary"
    },
    {
      key: "twitter-site",
      name: "twitter:site",
      content: "@Silversea"
    }
  ];

  if (image) {
    metas.push({
      key: "og-image",
      property: "og:image",
      content: image
    });
  }

  if (title) {
    metas.push(
      {
        key: "twitter-title",
        name: "twitter:title",
        content: title
      },
      {
        key: "og-title",
        property: "og:title",
        content: title
      }
    );
  }

  if (metaDescription) {
    metas.push(
      {
        key: "description",
        name: "description",
        content: metaDescription
      },
      {
        key: "og-description",
        property: "og:description",
        content: metaDescription
      },
      {
        key: "twitter-description",
        name: "twitter:description",
        content: metaDescription
      }
    );
  }

  return (
    <>
      <link rel="canonical" href={`https://www.silversea.com${path}`} />
      {excludeFromSitemap && (
        <meta key="robots" name="robots" content="noindex" />
      )}

      {alternateLinks.filter(isPresent).map(({ ogLangCode, path }) => (
        <link
          key={ogLangCode}
          rel="alternate"
          hrefLang={ogLangCode}
          href={`https://www.silversea.com${path}`}
        />
      ))}

      <script
        key="json-ld-generic"
        {...jsonLdScriptProps<WebSite>({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Silversea Cruises",
          url: "https://www.silversea.com/",
          sameAs: [
            "https://www.instagram.com/silverseacruises/",
            "https://twitter.com/Silversea",
            "https://www.youtube.com/user/SilverseaOfficial",
            "https://www.pinterest.com/silverseacruise/_created/",
            "https://www.facebook.com/silversea"
          ],
          potentialAction: {
            "@type": "SearchAction",
            target: "https://www.silversea.com/search.html?query={search_term}",
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            "query-input": "required name=search_term"
          }
        })}
      />
      {jsonLd &&
        jsonLd.map((j, index) => (
          <script key={`json-ld-specific-${index}`} {...j} />
        ))}
      {metas.map(({ key, ...meta }) => (
        <meta key={key} {...meta} />
      ))}

      {children}
      <title key="page-title">{pageTitle}</title>
    </>
  );
};

export default GatsbyHeadOpenGraph;

const query = graphql`
  query DefaultOpenGraphHead {
    site: sanitySite(
      _id: { in: ["site-silversea-com", "drafts.site-silversea-com"] }
    ) {
      openGraph {
        title {
          en
          es
          de
          fr
          pt_br
        }

        description {
          en
          es
          de
          fr
          pt_br
        }

        image {
          ...Image
        }
      }
    }
  }
`;
